/**
 * Arrange mode — the lock, borrowed from unRAID's dashboard.
 *
 * Cards are static until you unlock. That keeps drag handles off the screen for the 99% of
 * visits that are not rearranging, and it means a card body can hold its own drag targets
 * (the area rows, the task rows) without competing with a card-level drag that is not
 * currently listening.
 *
 * Follows you across a page change now (it used to lock itself on every navigation) — every
 * page can be deleted, including the one you are on, so "unlocked" has to survive being
 * bounced somewhere else by that, not just a deliberate click to another tab. Still not
 * persisted across a reload: this is plain `useState`, gone the moment `EditModeProvider`
 * itself remounts.
 */

import { useCallback, useMemo, useState } from "react";

import { EditModeContext, useEditMode } from "./editModeContext";
import { Lock, Unlock } from "../components/icons";

export function EditModeProvider({ children }: { children: React.ReactNode }) {
  const [editing, setEditing] = useState(false);

  const toggle = useCallback(() => setEditing((v) => !v), []);
  const value = useMemo(() => ({ editing, toggle }), [editing, toggle]);

  return <EditModeContext.Provider value={value}>{children}</EditModeContext.Provider>;
}

/** The lock itself. Lives in the nav, per the unRAID arrangement it is modelled on. */
export function ArrangeLock() {
  const { editing, toggle } = useEditMode();

  return (
    <button
      type="button"
      className={`arrange-lock${editing ? " unlocked" : ""}`}
      onClick={toggle}
      aria-pressed={editing}
      title={editing ? "Done arranging - lock the cards" : "Arrange the cards"}
    >
      {editing ? <Unlock size={17} /> : <Lock size={16} />}
      <span className="sr-only">{editing ? "Lock the layout" : "Arrange the layout"}</span>
    </button>
  );
}
